import React, { CSSProperties } from 'react';
import * as Classes from '../common/classes';
import {
  FinalTableComputedColumn,
  FinalTableProps,
  GroupStatus,
} from './FinalTableProps';
import { GridCellProps } from 'react-virtualized/dist/es/Grid';
import { IData } from '../virtual-data/VirtualData';
import classNames from 'classnames';
import { Deleted } from '../deleted/Deleted';
import { Skeleton } from '../skeleton/Skeleton';
import { mcRowCollapse, mcRowExpand } from '@mc/react-ui-icons';
import { Icon } from '../icon/Icon';
import { Spinner } from '../spinner/Spinner';
import {
  getColumnTitleAndValue,
  getFormattedValue,
  supportsAggregate,
} from './utils';
import { Tooltip } from '../tooltip/Tooltip';
import { ColumnAggregate } from './ColumnAggregate';
import { GetCellPropsData } from '../table/TableProps';
import { Link } from '../link/Link';

type Props<DATA extends IData<ID>, FILTER, ID> = {
  finalTableProps: FinalTableProps<DATA, FILTER, ID>;
  column: FinalTableComputedColumn<DATA>;
  gridCellProps: GridCellProps;
  isHovered?: boolean;
  setIsHover?: (isHovered: boolean) => void;
};

/**
 * Jedna buňka datového řádku (v body tabulky).
 * @param finalTableProps
 * @param formattedColumns
 * @param gridCellProps
 * @param isHovered
 * @constructor
 */
export const BodyCell = <DATA extends IData<ID>, FILTER, ID>({
  finalTableProps,
  column,
  gridCellProps,
  isHovered,
  setIsHover,
}: Props<DATA, FILTER, ID>) => {
  const rowData = finalTableProps.getRowByIndex(gridCellProps.rowIndex);
  const isGroupRow = finalTableProps.isGroupByIndex(gridCellProps.rowIndex);
  const isGroupCol =
    finalTableProps.grouping.length > 0 && gridCellProps.columnIndex === 0;
  const depth = finalTableProps.getDepthByIndex(gridCellProps.rowIndex);

  // ---
  let cellProps: GetCellPropsData | undefined = {};
  if (column.getCellProps) {
    // Pokud je definován getCellProps, použijeme ho pro získání props sloupce
    cellProps = column.getCellProps({
      rowIndex: gridCellProps.rowIndex,
      row: rowData,
    });
  }

  // ---
  const cellClassName = classNames(
    classNames(column.className, cellProps?.className),
    Classes.HOVER_CONTAINER_CELL,
    Classes.FINAL_TABLE_BODY_CELL,
    `depth-${depth}`,
    {
      [Classes.FINAL_TABLE_BODY_CHECK_CELL]:
        finalTableProps.config.checkable && column.check,
      [Classes.FINAL_TABLE_BODY_ACTION_CELL]: column.type === 'action',
      [Classes.FINAL_TABLE_BODY_GROUP_CELL]: isGroupRow,
      [Classes.FINAL_TABLE_BODY_GROUP_CELL_GRP]: isGroupCol,
      [Classes.CLICK_CONTAINER]: cellProps?.onClick || cellProps?.link,
    }
  );

  let cellContent = undefined;
  // Tooltip na buňce, může být níže ještě překryt string hodnotou buňky (výsledek volání cell()), ale jen pokud bude shownTitleCanOverride true
  let shownTitle = undefined;

  if (finalTableProps.config.checkable && column.check && column?.cell) {
    // ##
    // # Check column
    // ##

    cellContent = column.cell({
      rowIndex: gridCellProps.rowIndex,
      columnIndex: gridCellProps.columnIndex,
      // @ts-ignore
      row: rowData,
      column,
      value: finalTableProps.getRowRenderProps(gridCellProps.rowIndex).checked,
    });
  } else {
    if (rowData === null) {
      // ##
      // # Deleted row
      // ##

      // Ze serveru nebyla vrácena data - záznam byl smazán - zobrazíme jako "deleted"
      cellContent = (
        <div className={Classes.FINAL_TABLE_BODY_CELL_VALUE}>
          <Deleted />
        </div>
      );
    } else if (rowData === undefined) {
      // ##
      // # Loading row - skeleton
      // ##

      // Záznam ještě nebyl ze serveru načten - zobrazujeme jako loading
      if (isGroupRow) {
        if (gridCellProps.columnIndex === 0) {
          cellContent = (
            <div
              className={classNames(
                Classes.FINAL_TABLE_BODY_CELL_GROUP_CONTENT,
                Classes.FINAL_TABLE_BODY_CELL_GROUP_CONTENT_SKELETON,
                Classes.HOVER_CONTAINER_CELL
              )}
            >
              <Skeleton
                row={gridCellProps.rowIndex}
                col={gridCellProps.columnIndex}
              />
            </div>
          );
        } else {
          cellContent = (
            <div
              className={classNames(
                Classes.FINAL_TABLE_BODY_CELL_VALUE,
                `depth-${depth}`
              )}
            >
              <Skeleton
                row={gridCellProps.rowIndex}
                col={gridCellProps.columnIndex}
              />
            </div>
          );
        }
      } else {
        cellContent = (
          <div className={Classes.FINAL_TABLE_BODY_CELL_VALUE}>
            {column.type !== 'action' && !isGroupCol && (
              <Skeleton
                row={gridCellProps.rowIndex}
                col={gridCellProps.columnIndex}
              />
            )}
          </div>
        );
      }
    } else {
      // Záznam máme, liší se podle toho jestli je to standardní řádek nebo seskupovací řádek
      if (isGroupRow) {
        if (column.type !== 'action') {
          if (gridCellProps.columnIndex === 0) {
            // ##
            // # Group row - rozbalení/sbalení
            // ##

            const field = finalTableProps.grouping[depth].field;
            // @ts-ignore
            const cellValue = rowData[field];
            // @ts-ignore
            const count = rowData?.aggregate?.grpCount ?? 0;

            const groupStatus = finalTableProps.getGroupStatusByIndex(
              gridCellProps.rowIndex
            );

            const renderColumn =
              column.groupColumns?.[
                finalTableProps.getDepthByIndex(gridCellProps.rowIndex)
              ];

            cellContent = (
              <div
                className={classNames(
                  Classes.FINAL_TABLE_BODY_CELL_GROUP_CONTENT,
                  Classes.HOVER_CONTAINER_CELL
                )}
              >
                <div
                  className={Classes.FINAL_TABLE_BODY_CELL_GROUP_EXP_COL}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    finalTableProps.toggleExpand(gridCellProps.rowIndex);
                  }}
                >
                  {groupStatus === GroupStatus.EXPANDED && (
                    <Icon icon={mcRowCollapse} />
                  )}
                  {groupStatus === GroupStatus.COLLAPSED && (
                    <Icon icon={mcRowExpand} />
                  )}
                  {groupStatus === GroupStatus.LOADING && <Spinner size={5} />}
                </div>
                <div
                  className={classNames(
                    Classes.FINAL_TABLE_BODY_CELL_VALUE,
                    `depth-${depth}`
                  )}
                >
                  {renderColumn && renderColumn.cell
                    ? renderColumn.cell({
                        rowIndex: gridCellProps.rowIndex,
                        columnIndex: gridCellProps.columnIndex,
                        row: rowData,
                        column: renderColumn,
                        value: cellValue,
                        formattedValue: renderColumn.type
                          ? getFormattedValue(renderColumn, false, cellValue)
                          : undefined,
                      })
                    : cellValue}
                </div>
                <div
                  className={classNames(
                    Classes.FINAL_TABLE_GROUP_COUNT,
                    `depth-${depth}`
                  )}
                >
                  {count}
                </div>
              </div>
            );
          } else {
            // ##
            // # Aggregate sloupec
            // ##

            const field = column.accessor;
            let aggregate = field
              ? finalTableProps.getAggregate(field)
              : undefined;

            // @ts-ignore
            let cellValue = aggregate ? rowData?.aggregate[field] : undefined;
            shownTitle =
              column.columnTitle ??
              (column.accessor
                ? (getFormattedValue(column, true, cellValue) ?? cellValue)
                : undefined);
            cellContent = (
              <div
                className={classNames(
                  Classes.FINAL_TABLE_BODY_CELL_VALUE,
                  `depth-${depth}`
                )}
              >
                <ColumnAggregate
                  column={column}
                  finalTableProps={finalTableProps}
                  isHovered={isHovered}
                  setIsHover={setIsHover}
                />
                {supportsAggregate(column.type) && column.aggregatedCell
                  ? column.aggregatedCell({
                      rowIndex: gridCellProps.rowIndex,
                      columnIndex: gridCellProps.columnIndex,
                      row: rowData,
                      column,
                      value: cellValue,
                      formattedValue: column.aggregatedType
                        ? getFormattedValue(column, true, cellValue)
                        : undefined,
                    })
                  : cellValue}
              </div>
            );
          }
        }
      } else {
        // ##
        // # Standardní řádek - buňka s daty
        // ##

        let columnTitleAndValue = getColumnTitleAndValue(
          column,
          rowData,
          gridCellProps.rowIndex,
          gridCellProps.columnIndex
        );
        shownTitle = columnTitleAndValue.title;

        cellContent = (
          <div
            className={classNames(Classes.FINAL_TABLE_BODY_CELL_VALUE, {
              [Classes.FINAL_TABLE_BODY_CELL_GROUP_CONTENT]:
                finalTableProps.grouping.length > 0 &&
                gridCellProps.columnIndex === 0,
            })}
          >
            {columnTitleAndValue.cell}
          </div>
        );
      }
    }
  }

  // Pokus o to, aby group byly vždy vidět
  // const style = { ...gridCellProps.style };
  // if (isGroupRow && gridCellProps.columnIndex === 0) {
  //   style.left = gridCellProps.parent?.state?.scrollLeft ?? 0;
  //   style.zIndex = 1;
  // }

  // Merge style gridCellProps.style a cellProps.style
  let cellStyle: CSSProperties = {
    ...cellProps?.style,
    ...gridCellProps.style,
  };

  // ---
  let content;
  if (cellProps?.link) {
    content = (
      <Link
        key={gridCellProps.key}
        {...cellProps.link}
        title={typeof shownTitle === 'string' ? shownTitle : undefined}
        className={cellClassName}
        style={cellStyle}
      >
        {cellContent}
      </Link>
    );
  } else {
    content = (
      <div
        key={gridCellProps.key}
        title={typeof shownTitle === 'string' ? shownTitle : undefined}
        className={cellClassName}
        style={cellStyle}
        onClick={cellProps?.onClick}
      >
        {cellContent}
      </div>
    );
  }

  if (!shownTitle || typeof shownTitle === 'string') {
    return content;
  } else {
    return <Tooltip trigger={content}>{shownTitle}</Tooltip>;
  }
};
